import { PhotoIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const ACCEPT = { 'image/jpeg': [], 'image/png': [], 'image/webp': [] };

export interface FileUploadProps {
    /** Chosen file, not yet uploaded. */
    file: File | null;
    /** URL of the saved image when there is no pending file. */
    currentUrl?: string | null;
    onSelect: (file: File) => void;
    onRemove: () => void;
    /** Set false when the saved image cannot be deleted; Remove then only shows for a pending file. */
    canRemove?: boolean;
    aspect?: '4/3' | '1/1';
    /** 0-100 while uploading. */
    progress?: number;
    onCancel?: () => void;
    /** Upload error from the caller; type and size errors are produced here. */
    error?: string;
    label: string;
    /** Text alternative of the preview image (e.g. the item name). */
    alt?: string;
}

const formatSize = (bytes: number, locale: string): string => {
    const mb = bytes >= 1024 * 1024;
    return new Intl.NumberFormat(locale, { style: 'unit', unit: mb ? 'megabyte' : 'kilobyte', maximumFractionDigits: 1 }).format(mb ? bytes / 1024 / 1024 : bytes / 1024);
};

/** Drop zone with keyboard-operable browse button, preview, progress and replace/remove. */
export const FileUpload = ({ file, currentUrl, onSelect, onRemove, canRemove = true, aspect = '4/3', progress, onCancel, error, label, alt = '' }: FileUploadProps) => {
    const { t, i18n } = useTranslation();
    const [rejection, setRejection] = useState<string | null>(null);
    const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
    useEffect(() => () => (preview ? URL.revokeObjectURL(preview) : undefined), [preview]);

    const onDropRejected = (rejections: FileRejection[]) => {
        const code = rejections[0]?.errors[0]?.code;
        setRejection(code === 'file-too-large' ? t('ui.fileUpload.tooLarge') : t('ui.fileUpload.wrongType'));
    };
    const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
        accept: ACCEPT,
        maxSize: MAX_UPLOAD_BYTES,
        multiple: false,
        noClick: true,
        noKeyboard: true,
        onDropAccepted: ([accepted]) => {
            setRejection(null);
            onSelect(accepted);
        },
        onDropRejected,
    });

    const shown = preview ?? currentUrl ?? null;
    const message = rejection ?? error;
    const uploading = progress !== undefined;

    return (
        <div>
            <div
                {...getRootProps()}
                role="group"
                aria-label={label}
                className={clsx(
                    'rounded-card p-4',
                    shown ? 'bg-surface shadow-hairline' : 'border-2 border-dashed bg-surface p-6 text-center',
                    !shown && (isDragActive ? 'border-focus bg-tone-info-bg' : 'border-control'),
                    message && 'border-2 border-danger'
                )}
            >
                <input {...getInputProps()} aria-label={label} />
                {shown ? (
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                        <div className={clsx('w-full overflow-hidden rounded-control bg-sunken sm:w-48', aspect === '4/3' ? 'aspect-[4/3]' : 'aspect-square sm:w-32')}>
                            <img src={shown} alt={alt} className="size-full object-cover" />
                        </div>
                        <div className="min-w-0 flex-1">
                            {file && (
                                <p className="truncate text-small text-strong">
                                    {file.name} <span className="text-muted tabular-nums">({formatSize(file.size, i18n.language)})</span>
                                </p>
                            )}
                            {uploading ? (
                                <div className="mt-2">
                                    <div className="h-1 overflow-hidden rounded-chip bg-sunken" role="progressbar" aria-label={label} aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
                                        <div className="h-full bg-inverse" style={{ width: `${progress}%` }} />
                                    </div>
                                    <div className="mt-2 flex items-center justify-between">
                                        <span className="text-caption tabular-nums text-muted">{progress}%</span>
                                        {onCancel && (
                                            <Button variant="quiet" size="sm" onClick={onCancel}>
                                                {t('ui.cancel')}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-2 flex gap-2">
                                    <Button variant="outline" onClick={open}>
                                        {t('ui.fileUpload.replace')}
                                    </Button>
                                    {(canRemove || file) && (
                                        <Button variant="quiet" onClick={onRemove}>
                                            {t('ui.fileUpload.remove')}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="flex min-h-[8.75rem] flex-col items-center justify-center gap-1">
                        <PhotoIcon className="size-8 text-muted" aria-hidden="true" />
                        <p className="text-small text-strong">
                            {t('ui.fileUpload.drop')}{' '}
                            <button type="button" onClick={open} className="font-semibold text-link underline underline-offset-2">
                                {t('ui.fileUpload.browse')}
                            </button>
                        </p>
                        <p className="text-caption text-muted">{t('ui.fileUpload.constraints')}</p>
                    </div>
                )}
            </div>
            {message && (
                <p role="alert" className="mt-1.5 text-caption text-danger">
                    {message}
                </p>
            )}
        </div>
    );
};
